import React from "react";
import { useNavigate } from "react-router-dom";
import { useJudgeTiming } from "../hooks/useJudgeTiming";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

// ─────────────────────────────────────────────────────────────────────────────
// /home-2 — alternate landing page.
// Layout ported verbatim from the "disputefai-v2" static mockup. The original
// CSS lives in the scoped <style> block below: every selector is namespaced
// under #dfai-v2 (and the global resets/body styles are pinned to the wrapper)
// so this page's styling never leaks into the rest of the app.
//
// Interactive buttons are wired to the same destinations they have on the
// current /home (LandingPage): all "File a dispute" CTAs → /submit-dispute, and
// "Talk to the team" → the team mailto. Section/footer anchors scroll in-page.
// ─────────────────────────────────────────────────────────────────────────────

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,600;0,700;0,800;1,700&family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');

#dfai-v2 {
  --bg:        #F4F4F0;
  --bg-card:   #FFFFFF;
  --ink:       #111214;
  --ink-soft:  #3A3C40;
  --ink-muted: #7A7C82;
  --blue:      #2D7FF9;
  --blue-deep: #1A5EC4;
  --mint:      #1AC98E;
  --mint-bg:   #E6FAF4;
  --coral:     #FF4D2E;
  --coral-bg:  #FFF0ED;
  --border:    rgba(17,18,20,0.09);
  --border-md: rgba(17,18,20,0.14);
  --serif:     'Plus Jakarta Sans', sans-serif;
  --body:      'DM Sans', sans-serif;
  --mono:      'DM Mono', monospace;

  font-family: var(--body);
  background: var(--bg);
  color: var(--ink);
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
  min-height: 100vh;
  scroll-behavior: smooth;
  /* Flex column so SiteFooter's margin-top:auto pins it to the bottom, same
     shell as every other public page. */
  display: flex;
  flex-direction: column;
}

#dfai-v2 *, #dfai-v2 *::before, #dfai-v2 *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* ── Typography ── */
#dfai-v2 .display {
  font-family: var(--serif);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.03em;
}
#dfai-v2 .heading {
  font-family: var(--serif);
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.025em;
}
#dfai-v2 .mono {
  font-family: var(--mono);
  font-weight: 500;
  letter-spacing: 0.01em;
}

/* ── Utilities ── */
#dfai-v2 .container { max-width: 1200px; margin: 0 auto; padding: 0 32px; }
@media (max-width: 680px) { #dfai-v2 .container { padding: 0 20px; } }

#dfai-v2 .eyebrow {
  font-family: var(--mono);
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--blue-deep);
  margin-bottom: 20px;
}

#dfai-v2 .btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 22px;
  border-radius: 9px;
  font-family: var(--body);
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.18s ease;
  border: none;
}
#dfai-v2 .btn-primary { background: var(--ink); color: #fff; }
#dfai-v2 .btn-primary:hover { background: var(--blue); transform: translateY(-1px); }
#dfai-v2 .btn-ghost { background: transparent; color: var(--ink); border: 1.5px solid var(--border-md); }
#dfai-v2 .btn-ghost:hover { border-color: var(--ink); background: rgba(17,18,20,0.04); }
#dfai-v2 .btn-mint { background: var(--mint); color: #fff; }
#dfai-v2 .btn-mint:hover { background: #14b07c; transform: translateY(-1px); }

/* ── Win / Lose chips ── */
#dfai-v2 .chip-win {
  display: inline-flex; align-items: center; gap: 5px;
  background: var(--mint-bg); color: #0A7A53;
  font-size: 11px; font-weight: 600;
  letter-spacing: 0.06em; text-transform: uppercase;
  padding: 4px 10px; border-radius: 100px;
}
#dfai-v2 .chip-lose {
  display: inline-flex; align-items: center; gap: 5px;
  background: var(--coral-bg); color: #C4280F;
  font-size: 11px; font-weight: 600;
  letter-spacing: 0.06em; text-transform: uppercase;
  padding: 4px 10px; border-radius: 100px;
}
#dfai-v2 .chip-win::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--mint); }
#dfai-v2 .chip-lose::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--coral); }

/* ── Cards ── */
#dfai-v2 .card {
  background: var(--bg-card);
  border-radius: 16px;
  border: 1px solid var(--border);
  padding: 28px;
}

/* ── Verdict card ── */
#dfai-v2 .verdict-card {
  background: var(--bg-card);
  border-radius: 14px;
  border: 1px solid var(--border);
  overflow: hidden;
  box-shadow: 0 4px 24px -8px rgba(17,18,20,0.12);
}
#dfai-v2 .verdict-card-head {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
#dfai-v2 .verdict-card-body { padding: 18px 20px; }
#dfai-v2 .verdict-vs {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;
  align-items: center;
  margin-bottom: 14px;
}
#dfai-v2 .verdict-party {
  border-radius: 9px;
  padding: 12px 14px;
  text-align: center;
}
#dfai-v2 .verdict-party-win { background: var(--mint-bg); }
#dfai-v2 .verdict-party-lose { background: var(--coral-bg); }
#dfai-v2 .verdict-party-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 5px;
}
#dfai-v2 .verdict-party-win .verdict-party-label { color: #0A7A53; }
#dfai-v2 .verdict-party-lose .verdict-party-label { color: #C4280F; }
#dfai-v2 .verdict-party-name { font-size: 14px; font-weight: 600; color: var(--ink); }
#dfai-v2 .verdict-vs-text {
  font-family: var(--serif);
  font-size: 15px;
  font-weight: 700;
  color: var(--ink-muted);
  text-align: center;
}
#dfai-v2 .verdict-ruling {
  font-size: 13px;
  color: var(--ink-soft);
  line-height: 1.55;
  padding-top: 12px;
  border-top: 1px solid var(--border);
}
#dfai-v2 .verdict-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}
#dfai-v2 .verdict-meta-tag {
  font-size: 11px;
  color: var(--blue-deep);
  font-family: var(--mono);
}

/* NAV — the page's own nav markup was replaced by the sitewide SiteHeader.
   Its rules are deleted rather than left behind: "#dfai-v2 nav" is an element
   selector, so it was landing on SiteHeader's inner nav element (the links row)
   and giving it a sticky background and a border. */

/* HERO */
#dfai-v2 .hero { padding: 90px 0 80px; }
#dfai-v2 .hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: center;
}
@media (max-width: 860px) {
  #dfai-v2 .hero-grid { grid-template-columns: 1fr; gap: 48px; }
}
#dfai-v2 .hero h1 {
  font-size: clamp(38px, 4.8vw, 62px);
  margin-bottom: 24px;
  color: var(--ink);
}
#dfai-v2 .hero h1 .accent { color: var(--blue); }
#dfai-v2 .hero-lede {
  font-size: 18px;
  color: var(--ink-soft);
  line-height: 1.6;
  margin-bottom: 32px;
  max-width: 48ch;
}
#dfai-v2 .hero-actions { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 36px; }
#dfai-v2 .dispute-types { display: flex; flex-wrap: wrap; gap: 8px; }
#dfai-v2 .dtype {
  font-size: 12.5px; font-weight: 500;
  color: var(--ink-soft);
  background: rgba(17,18,20,0.06);
  border: 1px solid var(--border);
  border-radius: 100px;
  padding: 5px 13px;
  transition: all 0.15s;
}
#dfai-v2 .dtype:hover { background: var(--blue); color: #fff; border-color: var(--blue); cursor: pointer; }
#dfai-v2 .hero-cards {
  display: flex; flex-direction: column; gap: 14px;
  position: relative;
}
#dfai-v2 .hero-cards .verdict-card:nth-child(2) { transform: none; }
#dfai-v2 .hero-cards .verdict-card:nth-child(3) { transform: none; opacity: 1; }

/* TICKER */
#dfai-v2 .ticker-wrap {
  padding: 18px 0;
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  overflow: hidden;
  position: relative;
}
#dfai-v2 .ticker-wrap::before, #dfai-v2 .ticker-wrap::after {
  content: '';
  position: absolute;
  top: 0; bottom: 0;
  width: 80px;
  z-index: 2;
}
#dfai-v2 .ticker-wrap::before { left: 0; background: linear-gradient(to right, var(--bg), transparent); }
#dfai-v2 .ticker-wrap::after  { right: 0; background: linear-gradient(to left, var(--bg), transparent); }
#dfai-v2 .ticker-track {
  display: flex;
  gap: 0;
  animation: dfai-ticker 28s linear infinite;
  width: max-content;
}
#dfai-v2 .ticker-item {
  display: flex; align-items: center; gap: 10px;
  padding: 0 32px;
  font-size: 13px; font-weight: 500;
  color: var(--ink-soft);
  white-space: nowrap;
  border-right: 1px solid var(--border);
}
#dfai-v2 .ticker-dot { width: 7px; height: 7px; border-radius: 50%; }
#dfai-v2 .ticker-dot-mint { background: var(--mint); }
#dfai-v2 .ticker-dot-coral { background: var(--coral); }
@keyframes dfai-ticker {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}

/* HOW IT WORKS */
#dfai-v2 .how { padding: 120px 0; }
#dfai-v2 .how-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  margin-top: 56px;
  background: var(--border);
  border-radius: 16px;
  overflow: hidden;
}
@media (max-width: 860px) { #dfai-v2 .how-grid { grid-template-columns: 1fr 1fr; } }
@media (max-width: 520px) { #dfai-v2 .how-grid { grid-template-columns: 1fr; } }
#dfai-v2 .how-step {
  background: var(--bg-card);
  padding: 32px 28px;
  position: relative;
}
#dfai-v2 .how-step-num {
  font-family: var(--serif);
  font-size: 48px;
  font-weight: 800;
  color: rgba(17,18,20,0.07);
  line-height: 1;
  margin-bottom: 18px;
}
#dfai-v2 .how-step h4 {
  font-family: var(--serif);
  font-size: 19px;
  font-weight: 700;
  margin-bottom: 10px;
  letter-spacing: -0.01em;
}
#dfai-v2 .how-step p { font-size: 14px; line-height: 1.6; color: var(--ink-soft); }

/* DISPUTES FOR EVERYONE */
#dfai-v2 .everyone { padding: 120px 0; background: var(--ink); color: #fff; }
#dfai-v2 .everyone .eyebrow { color: var(--mint); }
#dfai-v2 .use-cases {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 56px;
}
@media (max-width: 860px) { #dfai-v2 .use-cases { grid-template-columns: 1fr; } }
#dfai-v2 .use-case {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 32px 28px;
  transition: background 0.2s;
}
#dfai-v2 .use-case:hover { background: rgba(255,255,255,0.07); }
#dfai-v2 .use-case-icon {
  width: 44px; height: 44px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px;
  margin-bottom: 22px;
}
#dfai-v2 .use-case h4 {
  font-family: var(--serif);
  font-size: 21px;
  font-weight: 700;
  margin-bottom: 10px;
  letter-spacing: -0.01em;
}
#dfai-v2 .use-case p { font-size: 14px; line-height: 1.6; color: rgba(255,255,255,0.6); }
#dfai-v2 .use-case-examples {
  margin-top: 18px;
  display: flex; flex-wrap: wrap; gap: 6px;
}
#dfai-v2 .use-case-ex {
  font-size: 12px;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.7);
  padding: 4px 10px;
  border-radius: 100px;
  border: 1px solid rgba(255,255,255,0.1);
}

/* JUDGES PANEL */
#dfai-v2 .panel { padding: 120px 0; }
#dfai-v2 .judges-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 56px;
}
@media (max-width: 760px) { #dfai-v2 .judges-grid { grid-template-columns: 1fr; } }
#dfai-v2 .judge-card {
  background: var(--bg-card);
  border-radius: 16px;
  border: 1px solid var(--border);
  padding: 32px 28px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
#dfai-v2 .judge-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 48px -16px rgba(17,18,20,0.15);
}
#dfai-v2 .judge-num {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--blue-deep);
  letter-spacing: 0.1em;
  margin-bottom: 16px;
}
#dfai-v2 .judge-card h4 {
  font-family: var(--serif);
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin-bottom: 4px;
}
#dfai-v2 .judge-provider {
  font-size: 13px;
  color: var(--ink-muted);
  font-weight: 500;
  margin-bottom: 18px;
}
#dfai-v2 .judge-card p { font-size: 14px; color: var(--ink-soft); line-height: 1.6; }
#dfai-v2 .panel-note {
  margin-top: 28px;
  padding: 22px 26px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 12px;
  display: flex; gap: 18px; align-items: flex-start;
}
#dfai-v2 .panel-note-icon {
  width: 36px; height: 36px; flex-shrink: 0;
  background: rgba(45,127,249,0.1);
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  color: var(--blue);
  font-size: 18px;
}
#dfai-v2 .panel-note p { font-size: 14.5px; color: var(--ink-soft); line-height: 1.6; }

/* PRICING */
#dfai-v2 .pricing { padding: 120px 0; background: var(--bg); }
#dfai-v2 .pricing-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 56px;
}
@media (max-width: 760px) { #dfai-v2 .pricing-grid { grid-template-columns: 1fr; } }
#dfai-v2 .tier {
  background: var(--bg-card);
  border-radius: 18px;
  border: 1.5px solid var(--border);
  padding: 40px 36px;
  display: flex; flex-direction: column;
  position: relative;
}
#dfai-v2 .tier-featured {
  background: var(--ink);
  border-color: var(--ink);
  box-shadow: 0 24px 64px -24px rgba(17,18,20,0.35);
}
#dfai-v2 .tier-badge {
  position: absolute; top: 20px; right: 20px;
  font-size: 11px; font-weight: 600;
  letter-spacing: 0.06em; text-transform: uppercase;
  background: var(--mint); color: #fff;
  padding: 4px 11px; border-radius: 100px;
}
#dfai-v2 .tier-eyebrow {
  font-family: var(--mono);
  font-size: 11px; font-weight: 500;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--blue-deep);
  margin-bottom: 14px;
}
#dfai-v2 .tier-featured .tier-eyebrow { color: rgba(255,255,255,0.5); }
#dfai-v2 .tier h3 {
  font-family: var(--serif);
  font-size: 28px; font-weight: 800;
  letter-spacing: -0.02em;
  margin-bottom: 10px;
}
#dfai-v2 .tier-featured h3 { color: #fff; }
#dfai-v2 .tier-sub { font-size: 14.5px; color: var(--ink-muted); margin-bottom: 28px; line-height: 1.55; }
#dfai-v2 .tier-featured .tier-sub { color: rgba(255,255,255,0.55); }
#dfai-v2 .tier-price {
  display: flex; align-items: baseline; gap: 5px;
  padding-bottom: 26px; margin-bottom: 26px;
  border-bottom: 1px solid var(--border);
}
#dfai-v2 .tier-featured .tier-price { border-bottom-color: rgba(255,255,255,0.1); }
#dfai-v2 .tier-price-big {
  font-family: var(--serif);
  font-size: 54px; font-weight: 800;
  letter-spacing: -0.03em; line-height: 1;
}
#dfai-v2 .tier-featured .tier-price-big { color: #fff; }
#dfai-v2 .tier-price-unit { font-size: 14px; color: var(--ink-muted); font-weight: 500; }
#dfai-v2 .tier-featured .tier-price-unit { color: rgba(255,255,255,0.45); }
#dfai-v2 .tier-list { list-style: none; margin: 0 0 32px; padding: 0; flex-grow: 1; }
#dfai-v2 .tier-list li {
  display: flex; align-items: flex-start; gap: 11px;
  padding: 11px 0;
  font-size: 14.5px; line-height: 1.5;
  border-top: 1px solid var(--border);
}
#dfai-v2 .tier-featured .tier-list li { border-top-color: rgba(255,255,255,0.07); color: rgba(255,255,255,0.85); }
#dfai-v2 .tier-list li.dim { color: var(--ink-muted); }
#dfai-v2 .tier-featured .tier-list li.dim { color: rgba(255,255,255,0.3); }
#dfai-v2 .tick { color: var(--mint); font-size: 14px; margin-top: 2px; flex-shrink: 0; }
#dfai-v2 .cross { color: var(--ink-muted); font-size: 14px; margin-top: 2px; flex-shrink: 0; opacity: 0.5; }
#dfai-v2 .tier-cta {
  display: block; text-align: center;
  padding: 14px 20px; border-radius: 10px;
  font-size: 15px; font-weight: 600;
  text-decoration: none; transition: all 0.18s;
  cursor: pointer; font-family: var(--body); width: 100%; border: none;
}
#dfai-v2 .tier .tier-cta {
  background: var(--bg);
  color: var(--ink);
  border: 1.5px solid var(--border-md);
}
#dfai-v2 .tier .tier-cta:hover { background: var(--ink); color: #fff; border-color: var(--ink); }
#dfai-v2 .tier-featured .tier-cta { background: var(--mint); color: #fff; border: none; }
#dfai-v2 .tier-featured .tier-cta:hover { background: #14b07c; }
#dfai-v2 .pricing-note {
  text-align: center; margin-top: 24px;
  font-size: 13px; color: var(--ink-muted);
}

/* THE CERTIFICATE */
#dfai-v2 .certificate-section { padding: 120px 0; background: var(--ink); }
#dfai-v2 .certificate-section .eyebrow { color: var(--mint); }
#dfai-v2 .certificate-section .heading { color: #fff; }
#dfai-v2 .certificate-section .section-sub { color: rgba(255,255,255,0.6); }
#dfai-v2 .cert-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 72px;
  align-items: center;
  margin-top: 60px;
}
@media (max-width: 860px) { #dfai-v2 .cert-inner { grid-template-columns: 1fr; gap: 48px; } }
#dfai-v2 .cert-perks { display: flex; flex-direction: column; gap: 4px; }
#dfai-v2 .cert-perk {
  display: flex; gap: 16px; align-items: flex-start;
  padding: 18px 0;
  border-top: 1px solid rgba(255,255,255,0.07);
}
#dfai-v2 .cert-perk:last-child { border-bottom: 1px solid rgba(255,255,255,0.07); }
#dfai-v2 .cert-perk-icon {
  width: 36px; height: 36px; flex-shrink: 0;
  border-radius: 8px;
  background: rgba(255,255,255,0.07);
  display: flex; align-items: center; justify-content: center;
  color: var(--mint); font-size: 17px;
}
#dfai-v2 .cert-perk h5 {
  font-family: var(--serif);
  font-size: 17px; font-weight: 700;
  margin-bottom: 4px; color: #fff;
}
#dfai-v2 .cert-perk p { font-size: 13.5px; color: rgba(255,255,255,0.55); line-height: 1.55; }
#dfai-v2 .cert-card {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 24px 64px -24px rgba(0,0,0,0.5);
}
#dfai-v2 .cert-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 5px;
  background: linear-gradient(to right, var(--mint), var(--blue));
}
#dfai-v2 .cert-label {
  font-family: var(--mono);
  font-size: 10.5px; font-weight: 500;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--blue-deep);
  margin-bottom: 6px;
}
#dfai-v2 .cert-title {
  font-family: var(--serif);
  font-size: 21px; font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin-bottom: 24px;
}
#dfai-v2 .cert-row {
  display: flex; justify-content: space-between;
  padding: 10px 0;
  border-top: 1px solid var(--border);
  font-size: 13px;
}
#dfai-v2 .cert-row:first-of-type { border-top: none; }
#dfai-v2 .cert-row-key { color: var(--blue-deep); font-family: var(--mono); font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; }
#dfai-v2 .cert-row-val { color: var(--ink); font-weight: 500; font-size: 13px; }
#dfai-v2 .cert-winner-row {
  margin-top: 18px;
  display: flex; justify-content: space-between;
  align-items: center;
  background: var(--mint-bg);
  border-radius: 10px;
  padding: 14px 16px;
}
#dfai-v2 .cert-winner-label { font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.1em; text-transform: uppercase; color: #0A7A53; }
#dfai-v2 .cert-winner-val { font-family: var(--serif); font-size: 18px; font-weight: 800; color: var(--ink); }
#dfai-v2 .cert-stamp {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px dashed rgba(17,18,20,0.15);
  display: flex; align-items: center; gap: 12px;
}
#dfai-v2 .cert-seal {
  width: 50px; height: 50px;
  border-radius: 50%;
  border: 2px solid var(--mint);
  display: flex; align-items: center; justify-content: center;
  transform: rotate(-9deg);
  color: var(--mint);
  font-family: var(--mono);
  font-size: 9px; font-weight: 500;
  text-align: center; line-height: 1.3;
  flex-shrink: 0;
}
#dfai-v2 .cert-stamp-text { font-size: 12px; color: var(--ink-muted); line-height: 1.5; }

/* FAQ */
#dfai-v2 .faq { padding: 120px 0; }
#dfai-v2 .faq-list { margin-top: 48px; border-top: 1px solid var(--border); }
#dfai-v2 details.faq-item { border-bottom: 1px solid var(--border); }
#dfai-v2 details.faq-item summary {
  display: flex; justify-content: space-between; align-items: center;
  padding: 24px 0; cursor: pointer; list-style: none; gap: 20px;
  transition: color 0.15s;
}
#dfai-v2 details.faq-item summary:hover { color: var(--blue); }
#dfai-v2 details.faq-item summary::-webkit-details-marker { display: none; }
#dfai-v2 .faq-q {
  font-family: var(--serif);
  font-size: 20px; font-weight: 700;
  letter-spacing: -0.01em; line-height: 1.3;
}
#dfai-v2 .faq-icon {
  width: 28px; height: 28px; flex-shrink: 0;
  background: rgba(17,18,20,0.06);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; font-weight: 300;
  transition: transform 0.22s, background 0.18s;
}
#dfai-v2 details[open] .faq-icon { transform: rotate(45deg); background: var(--ink); color: #fff; }
#dfai-v2 .faq-a { padding: 0 60px 24px 0; font-size: 15px; line-height: 1.7; color: var(--ink-soft); max-width: 66ch; }

/* FINAL CTA */
#dfai-v2 .final-cta {
  padding: 130px 0;
  text-align: center;
  position: relative;
  overflow: hidden;
}
#dfai-v2 .final-cta-bg {
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 70% 60% at 50% 80%, rgba(26,201,142,0.08) 0%, transparent 70%),
              radial-gradient(ellipse 50% 40% at 20% 30%, rgba(45,127,249,0.06) 0%, transparent 60%);
  pointer-events: none;
}
#dfai-v2 .final-cta h2 {
  font-size: clamp(38px, 5.5vw, 68px);
  max-width: 14ch; margin: 0 auto 22px;
}
#dfai-v2 .final-cta p {
  font-size: 18px; color: var(--ink-soft);
  max-width: 52ch; margin: 0 auto 40px; line-height: 1.6;
}
#dfai-v2 .final-cta-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

/* FOOTER */
#dfai-v2 footer {
  background: var(--ink); color: rgba(255,255,255,0.6);
  padding: 64px 0 32px;
}
#dfai-v2 .footer-inner {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 48px;
  padding-bottom: 48px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
@media (max-width: 860px) { #dfai-v2 .footer-inner { grid-template-columns: 1fr 1fr; gap: 36px; } }
@media (max-width: 520px) { #dfai-v2 .footer-inner { grid-template-columns: 1fr; } }
#dfai-v2 .footer-brand { font-size: 14px; line-height: 1.65; }
#dfai-v2 .footer-brand .logo { color: #fff; margin-bottom: 16px; display: inline-flex; }
#dfai-v2 .footer-col h6 {
  font-family: var(--mono);
  font-size: 10.5px; letter-spacing: 0.14em;
  text-transform: uppercase; color: var(--blue);
  margin-bottom: 16px; font-weight: 500;
}
#dfai-v2 .footer-col ul { list-style: none; }
#dfai-v2 .footer-col li { margin-bottom: 11px; }
#dfai-v2 .footer-col a { color: rgba(255,255,255,0.7); text-decoration: none; font-size: 14px; transition: color 0.15s; }
#dfai-v2 .footer-col a:hover { color: #fff; }
#dfai-v2 .footer-bottom {
  padding-top: 24px;
  display: flex; justify-content: space-between;
  flex-wrap: wrap; gap: 12px;
  font-size: 12.5px;
}
#dfai-v2 .footer-bottom a { color: rgba(255,255,255,0.45); text-decoration: none; }
#dfai-v2 .footer-bottom a:hover { color: rgba(255,255,255,0.8); }

/* SECTION HELPERS */
#dfai-v2 .section-header { margin-bottom: 0; }
#dfai-v2 .section-title { margin-bottom: 16px; }
#dfai-v2 .section-sub {
  font-size: 17.5px; color: var(--ink-soft);
  line-height: 1.6; max-width: 58ch;
}

/* ANIMATIONS */
@keyframes dfai-rise {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
#dfai-v2 .reveal { animation: dfai-rise 0.85s ease forwards; opacity: 0; }
#dfai-v2 .d1 { animation-delay: 0.05s; }
#dfai-v2 .d2 { animation-delay: 0.15s; }
#dfai-v2 .d3 { animation-delay: 0.25s; }
#dfai-v2 .d4 { animation-delay: 0.38s; }
#dfai-v2 .d5 { animation-delay: 0.50s; }

@media (prefers-reduced-motion: reduce) {
  #dfai-v2 .reveal { animation: none; opacity: 1; }
  #dfai-v2 .ticker-track { animation: none; }
}
`;

const TICKER_ITEMS = [
  { dot: "mint", text: "Unpaid contractor invoice — Verdict: ", strong: "Contractor paid", tail: " · 3–0" },
  { dot: "mint", text: "Security deposit withheld unfairly — Verdict: ", strong: "Tenant refunded", tail: " · 2–1" },
  { dot: "coral", text: "Sam owes $140 from the trip — Verdict: ", strong: "Sam pays", tail: " · 2–1" },
  { dot: "mint", text: "Goods not delivered as described — Verdict: ", strong: "Buyer refunded", tail: " · 3–0" },
  { dot: "coral", text: "Noise complaint between neighbours — Verdict: ", strong: "Tenant warned", tail: " · 2–1" },
  { dot: "mint", text: "Shared expenses dispute — Verdict: ", strong: "Even split ordered", tail: " · 3–0" },
  { dot: "coral", text: "LeBron vs Jordan GOAT — Verdict: ", strong: "Jordan", tail: " · 2–1" },
  { dot: "mint", text: "Service not completed to spec — Verdict: ", strong: "Partial refund", tail: " · 2–1" },
];

export default function HomeV2() {
  const navigate = useNavigate();
  const judgeTiming = useJudgeTiming();

  // Mirror the current /home behaviour: every "File a dispute" CTA opens the
  // case-submission flow (ProtectedRoute handles the login gate).
  const fileDispute = (e) => {
    if (e) e.preventDefault();
    navigate("/submit-dispute");
  };

  return (
    <div id="dfai-v2">
      <style>{css}</style>

      {/* ══ NAV ══ — the sitewide bar, same as every other public page. The
           page's own #dfai-v2 nav styles no longer apply to it (SiteHeader is
           inline-styled) and are left in the CSS block below only because this
           page is a verbatim port of the v2 mockup. */}
      <SiteHeader />

      {/* ══ HERO ══ */}
      <section className="hero" id="top">
        <div className="container">
          <div className="hero-grid">
            <div>
              <div className="eyebrow reveal d1">Independent AI arbitration service</div>
              <h1 className="display reveal d2">
                Fair. Fast.<br /><span className="accent">Resolved.</span>
              </h1>
              <p className="hero-lede reveal d3">
                disputefai is an independent arbitration service powered by a panel of three AI judges.
                Submit your case, invite the other party, and receive a reasoned verdict — sealed on the
                blockchain. From unpaid debts to everyday disagreements.
              </p>
              <div className="hero-actions reveal d4">
                <a href="#start" className="btn btn-primary" onClick={fileDispute}>File a dispute →</a>
                <a href="#how" className="btn btn-ghost">See how it works</a>
              </div>
              <div className="dispute-types reveal d5">
                <span className="dtype">💸 Unpaid debts</span>
                <span className="dtype">🤝 Contract disputes</span>
                <span className="dtype">🏠 Landlord &amp; tenant</span>
                <span className="dtype">🧾 Consumer complaints</span>
                <span className="dtype">⚽ Sports &amp; pop culture</span>
                <span className="dtype">+ any disagreement</span>
              </div>
            </div>

            <div className="hero-cards reveal d3">
              {/* Card 1 */}
              <div className="verdict-card">
                <div className="verdict-card-head">
                  <span className="mono" style={{ fontSize: 11, color: "var(--blue-deep)" }}>CASE · #4831</span>
                  <span className="chip-win">Verdict in</span>
                </div>
                <div className="verdict-card-body">
                  <div style={{ fontSize: "13.5px", fontWeight: 600, color: "var(--ink)", marginBottom: 6 }}>
                    Unpaid freelance invoice — $1,200 web design project
                  </div>
                  <div style={{ fontSize: "11.5px", color: "var(--ink-muted)", marginBottom: 14 }}>
                    Contract dispute · Filed 3 days ago
                  </div>
                  <div className="verdict-vs">
                    <div className="verdict-party verdict-party-win">
                      <div className="verdict-party-label">Winner</div>
                      <div className="verdict-party-name">M. Reyes</div>
                    </div>
                    <div className="verdict-vs-text">vs</div>
                    <div className="verdict-party verdict-party-lose">
                      <div className="verdict-party-label">Respondent</div>
                      <div className="verdict-party-name">Northbay Co.</div>
                    </div>
                  </div>
                  <div
                    className="verdict-ruling"
                    style={{ fontSize: "12.5px", paddingTop: 10, borderTop: "1px solid var(--border)", color: "var(--ink-soft)", lineHeight: 1.5 }}
                  >
                    Panel ruled 3–0 that the final payment is owed in full. Evidence of completed
                    deliverables was decisive.
                  </div>
                  <div className="verdict-meta" style={{ marginTop: 10 }}>
                    <span className="verdict-meta-tag">Panel: 3–0 unanimous</span>
                    <span className="chip-win">Sealed on chain</span>
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div className="verdict-card" style={{ opacity: 0.82 }}>
                <div className="verdict-card-head">
                  <span className="mono" style={{ fontSize: 11, color: "var(--blue-deep)" }}>CASE · #4829</span>
                  <span className="chip-win">Verdict in</span>
                </div>
                <div className="verdict-card-body">
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 14 }}>
                    Greatest of all time — Messi or Ronaldo?
                  </div>
                  <div className="verdict-vs">
                    <div className="verdict-party verdict-party-win">
                      <div className="verdict-party-label">Winner</div>
                      <div className="verdict-party-name">Messi</div>
                    </div>
                    <div className="verdict-vs-text">vs</div>
                    <div className="verdict-party verdict-party-lose">
                      <div className="verdict-party-label">Runner-up</div>
                      <div className="verdict-party-name">Ronaldo</div>
                    </div>
                  </div>
                  <div className="verdict-meta">
                    <span className="verdict-meta-tag">Panel: 3–0 unanimous</span>
                    <span className="chip-win">Sealed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TICKER ══ */}
      <div className="ticker-wrap">
        <div className="ticker-track">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((it, i) => (
            <div className="ticker-item" key={i}>
              <span className={`ticker-dot ticker-dot-${it.dot}`}></span>
              {it.text}<strong>{it.strong}</strong>{it.tail}
            </div>
          ))}
        </div>
      </div>

      {/* ══ HOW IT WORKS ══ */}
      <section className="how" id="how">
        <div className="container">
          <div className="section-header">
            <div className="eyebrow">The process</div>
            <h2 className="heading section-title" style={{ fontSize: "clamp(34px,4vw,50px)" }}>
              Four steps to a final word.
            </h2>
            <p className="section-sub">
              No lawyers. No scheduling. No waiting months for an answer. File, respond, deliberate, done.
            </p>
          </div>
          <div className="how-grid">
            <div className="how-step">
              <div className="how-step-num">01</div>
              <h4>You file the case.</h4>
              <p>Describe the dispute in plain language. Upload evidence if you have it. Choose one judge or three.</p>
            </div>
            <div className="how-step">
              <div className="how-step-num">02</div>
              <h4>They get their say.</h4>
              <p>The other side gets a free invitation to share their perspective. {judgeTiming.label} to respond — no pressure, no fee.</p>
            </div>
            <div className="how-step">
              <div className="how-step-num">03</div>
              <h4>The judges deliberate.</h4>
              <p>Each AI judge reviews the full case independently. No bias, no favoritism, no knowing each other's verdict.</p>
            </div>
            <div className="how-step">
              <div className="how-step-num">04</div>
              <h4>The verdict lands.</h4>
              <p>A synthesized ruling is delivered with full reasoning. Sealed on the blockchain. Yours to share forever.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ DISPUTES FOR EVERYONE ══ */}
      <section className="everyone">
        <div className="container">
          <div className="eyebrow">What gets resolved here</div>
          <h2 className="heading section-title" style={{ fontSize: "clamp(34px,4vw,50px)", color: "#fff" }}>
            Serious disputes.<br />Everyday ones too.
          </h2>
          <p className="section-sub">
            disputefai handles any case where two parties disagree and want a fair, independent verdict.
            Financial and contractual disputes are our foundation. Everything else is a natural extension
            of the same process.
          </p>
          <div className="use-cases">
            <div className="use-case">
              <div className="use-case-icon" style={{ background: "rgba(26,201,142,0.15)" }}>💸</div>
              <h4>Financial disputes</h4>
              <p>Unpaid invoices, shared expenses, security deposits, loans between friends. A fair verdict without lawyers, court dates, or months of waiting.</p>
              <div className="use-case-examples">
                <span className="use-case-ex">Unpaid invoices</span>
                <span className="use-case-ex">Security deposits</span>
                <span className="use-case-ex">Shared trip costs</span>
                <span className="use-case-ex">Loans between friends</span>
              </div>
            </div>
            <div className="use-case">
              <div className="use-case-icon" style={{ background: "rgba(45,127,249,0.15)" }}>🤝</div>
              <h4>Contract and service disputes</h4>
              <p>Work not delivered. Services below agreed standard. Goods that didn't match the description. Get a clear ruling on who was right.</p>
              <div className="use-case-examples">
                <span className="use-case-ex">Freelance contracts</span>
                <span className="use-case-ex">Landlord &amp; tenant</span>
                <span className="use-case-ex">Consumer complaints</span>
                <span className="use-case-ex">Service agreements</span>
              </div>
            </div>
            <div className="use-case">
              <div className="use-case-icon" style={{ background: "rgba(255,77,46,0.1)" }}>⚽</div>
              <h4>Everyday disagreements</h4>
              <p>Not every dispute involves a contract. Sports debates, pop culture arguments, who was right in a situation — the same rigorous process applies, just with lighter stakes.</p>
              <div className="use-case-examples">
                <span className="use-case-ex">Sports debates</span>
                <span className="use-case-ex">Pop culture</span>
                <span className="use-case-ex">Who was right</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ THE JUDGES ══ */}
      <section className="panel" id="panel">
        <div className="container">
          <div className="eyebrow">The panel</div>
          <h2 className="heading section-title" style={{ fontSize: "clamp(34px,4vw,50px)" }}>
            Three judges. Three providers. One ruling.
          </h2>
          <p className="section-sub">
            Every case is sent to three frontier AI models from three competing labs — independently, in
            parallel. Where they agree, you get confidence. Where they disagree, the synthesis weighs the
            strongest reasoning.
          </p>
          <div className="judges-grid">
            <div className="judge-card">
              <div className="judge-num">Judge 01</div>
              <h4>Claude</h4>
              <div className="judge-provider">Anthropic</div>
              <p>Reviews the full case with careful attention to nuance, context, and the strongest argument on each side.</p>
            </div>
            <div className="judge-card">
              <div className="judge-num">Judge 02</div>
              <h4>GPT-4</h4>
              <div className="judge-provider">OpenAI</div>
              <p>An independent second perspective from a different model family — no visibility into the other judges' verdicts.</p>
            </div>
            <div className="judge-card">
              <div className="judge-num">Judge 03</div>
              <h4>Gemini</h4>
              <div className="judge-provider">Google</div>
              <p>The third voice, and the synthesizer — notes where the panel agreed and disagreed before issuing the final decision.</p>
            </div>
          </div>
          <div className="panel-note">
            <div className="panel-note-icon">⚖</div>
            <p>
              <strong style={{ fontFamily: "var(--serif)", fontWeight: 700 }}>Why three?</strong> A single AI
              carries the biases of a single training run. Three independent models from three competing labs
              produce a verdict that is more defensible, more transparent, and harder to argue with at the next
              family dinner.
            </p>
          </div>
        </div>
      </section>

      {/* ══ PRICING ══ */}
      <section className="pricing" id="pricing">
        <div className="container">
          <div className="eyebrow">Two ways to file</div>
          <h2 className="heading section-title" style={{ fontSize: "clamp(34px,4vw,50px)" }}>
            Pick the panel that fits your stakes.
          </h2>
          <p className="section-sub">
            Low-stakes argument? One judge does the job. Something you actually want to win? Bring all three.
          </p>
          <div className="pricing-grid">
            <div className="tier">
              <div className="tier-eyebrow">Tier 01 · Single judge</div>
              <h3>One judge.</h3>
              <p className="tier-sub">A full ruling from Claude. Good for quick disputes and settling bar arguments on the spot.</p>
              <div className="tier-price">
                <div className="tier-price-big">$X</div>
                <div className="tier-price-unit">per case</div>
              </div>
              <ul className="tier-list">
                <li><span className="tick">✓</span>Full ruling from Claude with reasoning.</li>
                <li><span className="tick">✓</span>Conversational intake for both sides.</li>
                <li><span className="tick">✓</span>Written and media evidence supported.</li>
                <li><span className="tick">✓</span>Downloadable ruling document.</li>
                <li className="dim"><span className="cross">✕</span>Single perspective only.</li>
                <li className="dim"><span className="cross">✕</span>No blockchain certificate.</li>
              </ul>
              <button className="tier-cta" onClick={fileDispute}>File with one judge</button>
            </div>

            <div className="tier tier-featured">
              <div className="tier-badge">Recommended</div>
              <div className="tier-eyebrow">Tier 02 · Full panel</div>
              <h3>Three judges.</h3>
              <p className="tier-sub">The full panel. Three independent verdicts, a synthesized ruling, and a certificate they can never argue with.</p>
              <div className="tier-price">
                <div className="tier-price-big">$XX</div>
                <div className="tier-price-unit">per case</div>
              </div>
              <ul className="tier-list">
                <li><span className="tick">✓</span>Independent rulings from Claude, GPT-4, and Gemini.</li>
                <li><span className="tick">✓</span>Synthesized final verdict with full reasoning.</li>
                <li><span className="tick">✓</span>Blockchain-stamped certificate to share forever.</li>
                <li><span className="tick">✓</span>Optional monetary stake — winner takes the pot.</li>
                <li><span className="tick">✓</span>Permanent case URL and shareable verdict card.</li>
                <li><span className="tick">✓</span>Evidence hashed for tamper-evident integrity.</li>
              </ul>
              <button className="tier-cta" onClick={fileDispute}>Convene the full panel</button>
            </div>
          </div>
          <p className="pricing-note">Tier is chosen at submission. Rulings are non-binding and do not constitute legal advice.</p>
        </div>
      </section>

      {/* ══ CERTIFICATE ══ */}
      <section className="certificate-section">
        <div className="container">
          <div className="eyebrow">The proof</div>
          <h2 className="heading section-title" style={{ fontSize: "clamp(34px,4vw,50px)", color: "#fff" }}>
            A verdict they<br />can't take back.
          </h2>
          <p className="section-sub">
            Every full-panel ruling gets sealed on the blockchain — a tamper-evident record that lives forever.
            Not a screenshot. Not a text message. A certificate.
          </p>
          <div className="cert-inner">
            <div className="cert-perks">
              <div className="cert-perk">
                <div className="cert-perk-icon">🔒</div>
                <div>
                  <h5>Cryptographic seal</h5>
                  <p>A SHA-256 hash of the ruling is registered on-chain via Chainletter Labs the moment it's issued.</p>
                </div>
              </div>
              <div className="cert-perk">
                <div className="cert-perk-icon">📎</div>
                <div>
                  <h5>Evidence integrity</h5>
                  <p>Every uploaded file is hashed at submission. No one can quietly swap out the evidence afterward.</p>
                </div>
              </div>
              <div className="cert-perk">
                <div className="cert-perk-icon">🔗</div>
                <div>
                  <h5>Share it anywhere</h5>
                  <p>A permanent case URL and a shareable verdict card — built for group chats, not courtrooms.</p>
                </div>
              </div>
            </div>

            <div className="cert-card">
              <div className="cert-label">Certificate of ruling</div>
              <div className="cert-title">Unpaid invoice —<br />web design contract</div>
              <div className="cert-row">
                <span className="cert-row-key">Case</span>
                <span className="cert-row-val">CASE-2026-04831</span>
              </div>
              <div className="cert-row">
                <span className="cert-row-key">Issued</span>
                <span className="cert-row-val">22 May 2026 · 17:42 UTC</span>
              </div>
              <div className="cert-row">
                <span className="cert-row-key">Panel</span>
                <span className="cert-row-val">Claude · GPT-4 · Gemini</span>
              </div>
              <div className="cert-row">
                <span className="cert-row-key">Hash</span>
                <span className="cert-row-val" style={{ fontFamily: "var(--mono)", fontSize: 11 }}>0x9c2e…f14a</span>
              </div>
              <div className="cert-winner-row">
                <span className="cert-winner-label">Unanimous winner</span>
                <span className="cert-winner-val">M. Reyes · 3–0</span>
              </div>
              <div className="cert-stamp">
                <div className="cert-seal">SEALED<br />05·26</div>
                <div className="cert-stamp-text">Tamper-evident.<br />Verify at any time at disputefai.com/case/4831</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ FAQ ══ */}
      <section className="faq" id="faq">
        <div className="container">
          <div className="eyebrow">Common questions</div>
          <h2 className="heading section-title" style={{ fontSize: "clamp(34px,4vw,50px)" }}>
            Things people ask.
          </h2>
          <div className="faq-list">
            <details className="faq-item">
              <summary><span className="faq-q">What kinds of arguments can I file?</span><span className="faq-icon">+</span></summary>
              <p className="faq-a">Anything where two people genuinely disagree and want a legitimate answer. Sports debates, pop culture arguments, who owes who money from a trip, who was right in a disagreement, political opinions — even "is a hot dog a sandwich." The only disputes we decline are criminal matters and anything involving immediate personal safety.</p>
            </details>
            <details className="faq-item">
              <summary><span className="faq-q">Does the other person have to pay anything?</span><span className="faq-icon">+</span></summary>
              <p className="faq-a">No. The person who files pays the platform fee. The other side responds at no cost. This keeps the invitation feel friendly rather than adversarial — they're being asked to share their side, not pay to defend themselves.</p>
            </details>
            <details className="faq-item">
              <summary><span className="faq-q">What if the other person ignores the invitation?</span><span className="faq-icon">+</span></summary>
              <p className="faq-a">After {judgeTiming.label} without a response, the case proceeds on the available record. The judges are informed that the other party chose not to respond, and the ruling is issued and marked "uncontested." A verdict that says one side didn't even show up to argue carries its own weight.</p>
            </details>
            <details className="faq-item">
              <summary><span className="faq-q">How does the monetary stake work?</span><span className="faq-icon">+</span></summary>
              <p className="faq-a">Filing a case requires a minimum deposit of $10, which goes into escrow. You can deposit more than the minimum if you want to raise the stakes. The other party must match your deposit exactly for the case to proceed — if they choose not to, the case does not move forward and your deposit is returned in full. Once both deposits are in escrow, the panel deliberates and the winner receives 75% of the combined pot. disputefai retains 25% as a service fee.</p>
            </details>
            <details className="faq-item">
              <summary><span className="faq-q">Why three judges instead of one?</span><span className="faq-icon">+</span></summary>
              <p className="faq-a">Every AI model carries the subtle biases of its training. One model might lean a certain direction on a cultural question that another wouldn't. Three independent models from three competing companies produce a verdict that is harder to dismiss — if all three agree, you have something genuinely defensible.</p>
            </details>
            <details className="faq-item">
              <summary><span className="faq-q">Are the rulings legally binding?</span><span className="faq-icon">+</span></summary>
              <p className="faq-a">No. disputefai verdicts are reasoned, multi-model arbitration opinions — not legal judgments. They're designed to settle arguments between people who respect a fair process, not to replace courts or lawyers. For anything that genuinely needs legal resolution, please consult an attorney.</p>
            </details>
            <details className="faq-item">
              <summary><span className="faq-q">How long does it take to get a verdict?</span><span className="faq-icon">+</span></summary>
              <p className="faq-a">Once both sides have submitted their testimony, the panel usually returns a verdict within minutes. The {judgeTiming.label} window is for the other side to respond — if they respond immediately, you can have a sealed verdict the same day you file.</p>
            </details>
          </div>
        </div>
      </section>

      {/* ══ HOW THE POT WORKS ══ */}
      <section style={{ padding: "120px 0", background: "var(--bg)" }}>
        <div className="container">
          <div className="eyebrow">How the money works</div>
          <h2 className="heading section-title" style={{ fontSize: "clamp(34px,4vw,50px)" }}>
            How the deposit<br />and pot work.
          </h2>
          <p className="section-sub" style={{ marginBottom: 60 }}>
            Both parties put a deposit into escrow before the case proceeds. The deposit confirms each side is
            engaged and serious. Here is exactly how the escrow, panel, and payout work.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div className="card" style={{ borderLeft: "3px solid var(--blue)" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--blue-deep)", marginBottom: 12 }}>Step 1 · Person 1 files</div>
              <h4 style={{ fontFamily: "var(--serif)", fontSize: 19, fontWeight: 700, marginBottom: 8, letterSpacing: "-0.01em" }}>A minimum $10 deposit opens the case.</h4>
              <p style={{ fontSize: "14.5px", color: "var(--ink-soft)", lineHeight: 1.6 }}>The filer puts a minimum of $10 into escrow. This is held by disputefai and is not paid to anyone until the verdict is reached. The filer can deposit more to raise the stakes.</p>
            </div>
            <div className="card" style={{ borderLeft: "3px solid var(--mint)" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A7A53", marginBottom: 12 }}>Step 2 · Person 2 responds</div>
              <h4 style={{ fontFamily: "var(--serif)", fontSize: 19, fontWeight: 700, marginBottom: 8, letterSpacing: "-0.01em" }}>Matching the deposit confirms participation.</h4>
              <p style={{ fontSize: "14.5px", color: "var(--ink-soft)", lineHeight: 1.6 }}>The respondent matches the deposit to confirm they are engaging with the process in good faith. If they do not respond within {judgeTiming.label}, the filer's deposit is returned in full and a default ruling is issued.</p>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 36 }}>
            <div className="card" style={{ borderLeft: "3px solid var(--ink-muted)" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--blue-deep)", marginBottom: 12 }}>Step 3 · Panel deliberates</div>
              <h4 style={{ fontFamily: "var(--serif)", fontSize: 19, fontWeight: 700, marginBottom: 8, letterSpacing: "-0.01em" }}>Three AI judges review the full record.</h4>
              <p style={{ fontSize: "14.5px", color: "var(--ink-soft)", lineHeight: 1.6 }}>Neither party can influence the panel. The judges receive the complete case record — testimony, evidence, and any supporting documents — and deliberate independently before a verdict is synthesized.</p>
            </div>
            <div className="card" style={{ borderLeft: "3px solid var(--mint)" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A7A53", marginBottom: 12 }}>Step 4 · Verdict and disbursement</div>
              <h4 style={{ fontFamily: "var(--serif)", fontSize: 19, fontWeight: 700, marginBottom: 8, letterSpacing: "-0.01em" }}>The winner receives 75% of the pot.</h4>
              <p style={{ fontSize: "14.5px", color: "var(--ink-soft)", lineHeight: 1.6 }}>Once the verdict is issued, the escrow is released. The winning party receives 75% of the total pot. The remaining 25% is retained by disputefai as a service fee for providing the arbitration infrastructure.</p>
            </div>
          </div>

          <div style={{ background: "var(--bg-card)", border: "1.5px solid var(--border-md)", borderRadius: 14, padding: "28px 32px", display: "flex", gap: 20, alignItems: "flex-start" }}>
            <div style={{ fontSize: 28, flexShrink: 0 }}>⚖️</div>
            <div>
              <h5 style={{ fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700, marginBottom: 6, letterSpacing: "-0.01em" }}>disputefai has no stake in the outcome.</h5>
              <p style={{ fontSize: "14.5px", color: "var(--ink-soft)", lineHeight: 1.65 }}>Both deposits sit in escrow until the verdict is reached. disputefai holds the funds as a neutral third party and disburses based solely on the panel's ruling. The 25% service fee is the same regardless of which party wins. The outcome is determined by the strength of each party's argument and evidence, reviewed independently by three AI judges.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ FINAL CTA ══ */}
      <section className="final-cta" id="start">
        <div className="final-cta-bg"></div>
        <div className="container">
          <div className="eyebrow">Ready to resolve it?</div>
          <h2 className="display" style={{ color: "var(--ink)" }}>
            A fair hearing.<br /><span style={{ color: "var(--mint)" }}>A final answer.</span>
          </h2>
          <p>File a case in minutes. Both sides are heard. A panel of three AI judges delivers a verdict — sealed on the blockchain and settled for good.</p>
          <div className="final-cta-actions">
            <a href="#" className="btn btn-primary" style={{ fontSize: 16, padding: "15px 28px" }} onClick={fileDispute}>File a dispute →</a>
            <a href="mailto:hello@disputefai.com" className="btn btn-ghost" style={{ fontSize: 16, padding: "15px 28px" }}>Talk to the team</a>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══ — sitewide SiteFooter, same as every other page. */}
      <SiteFooter />
    </div>
  );
}
